import React, { useEffect, useState, useReducer, useMemo } from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { conformToMask } from 'react-text-mask'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import Breadcrumb from 'components/Breadcrumb'
import Loader from 'components/Loader'
import Card from 'components/Card'
import Button from 'components/Button'
import SimpleListingTable from 'components/SimpleListingTable'
import DataListingTable from 'components/DataListingTable'
import { Text, SemiTitle, Title } from 'components/Typography'
import { ActionModal, DefaultModal } from 'components/Modal'
import { Close, Plus as PlusIcon, Edit as EditIcon } from 'components/Icons'
import { DateRangePickerField, NumberField } from 'components/Form'

import { reducer } from 'helpers/loadingReducer'
import { getPartner, deactivatePartner as deactivateRequest, activatePartner, addDiscount } from 'services/partners'
import { getConsumptions } from 'services/consumptions'
import * as packagesBalanceService from 'services/packagesBalance'

import { phoneMask } from 'helpers/masks'
import { formatDocumentNumber } from 'utils/formatters'

import { colors } from 'styles/theme'
import { ChangeDiscountButton } from './styles'

const discountFormSchema = Yup.object().shape({
  discount_value: Yup.number()
    .max(100, 'Desconto máximo é de 100%')
    .required(),
})

const tableColumnsPackages = [
  {
    Header: 'Pacote',
    accessor: 'package_name',
  },
  {
    Header: 'Saldo',
    accessor: 'package_balance',
  },
  {
    Header: 'Preço',
    accessor: 'package_price',
  },
  {
    Header: 'Ações',
    accessor: 'actions',
  },
]

const tableColumnsConsumption = [
  {
    Header: 'Pacote',
    accessor: 'package_name',
    width: '50%',
    disableSortBy: true,
  },
  {
    Header: 'Beneficiário',
    accessor: 'beneficiary',
    width: '50%',
    disableSortBy: true,
  },
  {
    Header: 'Email',
    accessor: 'email',
    width: '50%',
    disableSortBy: true,
  },
  {
    Header: 'CPF',
    accessor: 'document',
    width: '50%',
    disableSortBy: true,
  },
  {
    Header: 'Data',
    accessor: 'created_at',
    width: '50%',
    disableSortBy: true,
  },
]

const formatConsumptionsData = consumptions => {
  return consumptions.map(consumption => {
    return {
      package_name: consumption.package_object.name,
      beneficiary: consumption.beneficiary.name,
      created_at: moment(consumption.created_at).format('DD/MM/YYYY'),
      document: formatDocumentNumber(consumption.beneficiary.document),
      email: consumption.beneficiary.email,
    }
  })
}

const initialValues = {
  date: {
    startDate: '',
    endDate: '',
  },
}

const getFormatedCurrentDiscount = currentDiscount => {
  if (!currentDiscount) {
    return '-'
  }

  return new Intl.NumberFormat('pt-BR', { style: 'unit', unit: 'percent' }).format(currentDiscount)
}

const Show = () => {
  const { id } = useParams()
  const [partner, setPartner] = useState({})
  const [consumptions, setConsumptions] = useState([])
  const [requestStatus, dispatch] = useReducer(reducer, { isLoading: true, error: false })
  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })
  const [isOpened, setIsOpened] = useState(false)
  const [isActivationModalOpened, setIsActivationModalOpened] = useState(false)
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [isActivating, setIsActivating] = useState(false)
  const [isUserActive, setIsUserActive] = useState(true)
  const [dateFilter, setDateFilter] = useState({})
  const [isDiscountDefinitionModalOpen, setDiscountDefinitionModalOpen] = useState(false)

  const breadcrumbPath = useMemo(
    () => [
      {
        label: 'Lista de parceiros pré pago',
        link: '/staff/partners/list',
      },
      {
        label: partner?.company_name,
      },
    ],
    [partner],
  )

  const handleTableDataFetch = ({ sortBy, currentPage, pageSize }) => {
    setSortBy(sortBy)
    setPagination({ ...pagination, currentPage, pageSize })
  }

  const handleSubmit = ({ date }) =>
    setDateFilter({
      ...(date && date.startDate && { start_date: moment(date.startDate._d).format('YYYYMMDD') }),
      ...(date && date.endDate && { end_date: moment(date.endDate._d).format('YYYYMMDD') }),
    })

  const handleSubmitDiscount = async ({ discount_value }) => {
    if (discount_value !== partner?.current_discount) {
      try {
        await addDiscount(id, { value: discount_value })
        toast.success('Desconto atualizado com sucesso!')
        setDiscountDefinitionModalOpen(false)
      } catch (e) {
        console.log(e)
        toast.error('Não foi possível definir o novo desconto do parceiro.')
      }
    } else {
      setDiscountDefinitionModalOpen(false)
    }
  }

  const deactivatePartner = async () => {
    try {
      setIsDeactivating(true)
      await deactivateRequest(id)

      toast.success('Parceiro desativado com sucesso.')
      setIsUserActive(false)
    } catch (e) {
      console.log(e)
      toast.error('Não foi possivel desativar o parceiro.')
    } finally {
      setIsDeactivating(false)
    }
  }

  const reActivatePartner = async () => {
    try {
      setIsActivating(true)
      await activatePartner(id)

      toast.success('Parceiro desativado com sucesso.')
      setIsActivationModalOpened(false)
      setIsUserActive(true)
    } catch (e) {
      console.log(e)
      toast.error('Não foi possivel desativar o parceiro.')
    } finally {
      setIsActivating(false)
    }
  }

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const {
          data: { phones, prices, created_at, deleted_at, current_discount, ...data },
        } = await getPartner(id)

        const { conformedValue: formatedPhone } = conformToMask(phones && phones[0]?.number, phoneMask, {
          guide: false,
        })

        const formatedCreatedAt = new Intl.DateTimeFormat('pt-BR', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        }).format(new Date(created_at))

        const partnerBalance = await packagesBalanceService.getPartnerBalance(id)

        const formatedPrices = prices.map(({ package_price, ...rest }) => ({
          ...rest,
          package_price: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(package_price),
          package_balance: partnerBalance?.find(({ id }) => id === rest.package_id)?.balance || 0,
          actions: (
            <Text as={Link} to={`/staff/partners/list/${id}/prices/${rest.package_id}/edit`} textDecoration='none'>
              <EditIcon /> Editar Preço
            </Text>
          ),
        }))

        if (deleted_at) setIsUserActive(false)

        setPartner({
          ...data,
          phone: formatedPhone || '-',
          created_at: formatedCreatedAt,
          prices: formatedPrices,
          current_discount_formated: getFormatedCurrentDiscount(current_discount?.value),
          current_discount: current_discount?.value || 0,
        })
        dispatch({ type: 'success' })
      } catch (e) {
        console.log(e)
        toast.error('Não foi possível carregar os dados do Parceiro.')
        dispatch({ type: 'error' })
      }
    }

    fetchPartner()
  }, [isActivationModalOpened, isOpened, isDiscountDefinitionModalOpen])

  useEffect(() => {
    const getConsumptionData = async () => {
      try {
        const res = await getConsumptions({ partner_id: id, ...dateFilter, pagination, sortBy })
        setConsumptions(formatConsumptionsData(res.consumptions))
        setPagination(res.pagination)
      } catch (error) {
        console.log(error)
        toast.error('Erro ao carregar dados de consumo do parceiro!')
      }
    }

    getConsumptionData()
  }, [pagination.currentPage, pagination.pageSize, dateFilter])

  return (
    <Flex flexDirection='column'>
      {requestStatus.isLoading ? (
        <Flex width={1} height={268} alignItems='center' justifyContent='center'>
          <Loader />
        </Flex>
      ) : (
        <>
          <Breadcrumb children={breadcrumbPath} />
          <Flex alignItems='center' justifyContent='space-between' width={1} mb={32}>
            <Title as='h3'>{partner?.company_name}</Title>
            <Flex justifyContent='flex-end' width={2 / 3}>
              <Link to={`/staff/partners/list/${id}/beneficiaries`}>
                <Button mr={24} style={{ width: 'auto' }}>
                  Lista de beneficiários
                </Button>
              </Link>
              <Link to={`/staff/partners/list/${id}/edit`}>
                <Button outlined>Editar</Button>
              </Link>
            </Flex>
          </Flex>

          <Card minHeight={268}>
            {requestStatus.error ? (
              <Text>Não foi possivel carregar os dados do beneficiário no momento.</Text>
            ) : (
              <Flex justifyContent='space-between'>
                <Flex flexDirection='column' flex={1}>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>Razão Social</SemiTitle>
                    <Text>{partner?.company_name}</Text>
                  </Box>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>Nome do Responsável</SemiTitle>
                    <Text>{partner?.responsible_name}</Text>
                  </Box>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>Telefone do Responsável</SemiTitle>
                    <Text>{partner?.phone}</Text>
                  </Box>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>Desconto Atual</SemiTitle>
                    <Flex>
                      <Text>{partner?.current_discount_formated}</Text>
                      {partner?.current_discount_formated === '-' ? (
                        <ChangeDiscountButton type='button' onClick={() => setDiscountDefinitionModalOpen(true)}>
                          <PlusIcon /> Definir
                        </ChangeDiscountButton>
                      ) : (
                        <ChangeDiscountButton type='button' onClick={() => setDiscountDefinitionModalOpen(true)}>
                          <EditIcon /> Alterar
                        </ChangeDiscountButton>
                      )}
                    </Flex>
                  </Box>

                  <Button
                    variant={isUserActive ? 'tertiary' : 'primary'}
                    size='big'
                    mr={24}
                    onClick={() => (isUserActive ? setIsOpened(true) : setIsActivationModalOpened(true))}
                    outline={isUserActive}
                  >
                    {isUserActive ? 'Desativar' : 'Ativar'} parceiro
                  </Button>
                </Flex>
                <Flex flexDirection='column' flex={1}>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>CNPJ</SemiTitle>
                    <Text>
                      {partner?.document
                        ? partner?.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4')
                        : '-'}
                    </Text>
                  </Box>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>Email do Responsável</SemiTitle>
                    <Text>{partner?.user?.email}</Text>
                  </Box>
                  <Box mb={32}>
                    <SemiTitle color='grays.N500'>Cadastrado realizado em</SemiTitle>
                    <Text>{partner?.created_at}</Text>
                  </Box>
                </Flex>
              </Flex>
            )}
          </Card>

          <Flex alignItems='center' justifyContent='space-between' width={1} my={32}>
            <Title as='h3'>Informações dos Pacotes</Title>
          </Flex>

          <SimpleListingTable columns={tableColumnsPackages} data={partner?.prices} />

          <Flex alignItems='center' justifyContent='space-between' width={1} my={32}>
            <Title as='h3'>Histórico de Consumo</Title>
          </Flex>

          <Formik onSubmit={handleSubmit} initialValues={initialValues}>
            <Form>
              <Flex justifyContent='space-between' mb={32}>
                <DateRangePickerField name='date' placeholder='Data' />
                <Button type='submit'>Filtrar</Button>
              </Flex>
            </Form>
          </Formik>

          <DataListingTable
            columns={tableColumnsConsumption}
            data={consumptions}
            pagination={pagination}
            fetchData={handleTableDataFetch}
          />
        </>
      )}
      <ActionModal
        isOpened={isOpened}
        setIsOpened={setIsOpened}
        title='Desativar parceiro'
        text='Você tem certeza que deseja desativar este parceiro?'
        onDelete={deactivatePartner}
        status={isDeactivating}
      />
      <DefaultModal isOpened={isActivationModalOpened} setIsOpened={setIsActivationModalOpened}>
        <Card backgroundColor={colors.white} width={588}>
          <Flex flexDirection='column'>
            <Flex alignItems='center' justifyContent='space-between' mb={20}>
              <SemiTitle>Ativar parceiro</SemiTitle>
              <CloseButton onClick={() => setIsActivationModalOpened(false)}>
                <Close />
              </CloseButton>
            </Flex>
            <Box mb={30}>
              <Text>Você tem certeza que deseja ativar esse beneficiário?</Text>
            </Box>
            <Flex alignItems='center' justifyContent='flex-end'>
              <Button outlined onClick={() => setIsActivationModalOpened(false)}>
                Cancelar
              </Button>
              <Button disabled={isActivating} onClick={reActivatePartner} marginLeft={12}>
                {isActivating ? <Loader /> : 'Ativar'}
              </Button>
            </Flex>
          </Flex>
        </Card>
      </DefaultModal>
      <DefaultModal isOpened={isDiscountDefinitionModalOpen} setIsOpened={setDiscountDefinitionModalOpen}>
        <Card backgroundColor={colors.white} width={480}>
          <Flex flexDirection='column'>
            <Flex alignItems='center' justifyContent='space-between' mb={20}>
              <SemiTitle>Definir um novo desconto para o parceiro</SemiTitle>
              <CloseButton onClick={() => setDiscountDefinitionModalOpen(false)}>
                <Close />
              </CloseButton>
            </Flex>
            <Formik
              initialValues={{ discount_value: partner?.current_discount }}
              onSubmit={handleSubmitDiscount}
              validationSchema={discountFormSchema}
            >
              {({ isSubmitting }) => (
                <Form>
                  <Box mb={30}>
                    <NumberField
                      label='Novo desconto'
                      name='discount_value'
                      placeholder='10,00 %'
                      suffix=' %'
                      decimalScale='2'
                    />
                  </Box>
                  <Flex alignItems='center' justifyContent='flex-end'>
                    <Button outlined onClick={() => setDiscountDefinitionModalOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type='submit' marginLeft={12} disabled={isSubmitting}>
                      {isSubmitting ? <Loader /> : 'Salvar'}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </Card>
      </DefaultModal>
    </Flex>
  )
}

const CloseButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
`

export default Show
